import React, { useState, useEffect } from 'react'
import produce from 'immer'
import { useDebounce } from 'use-lodash-debounce'
import { v4 as uuid } from 'uuid';

import InputTable from './index'
import { EditWrapper, EditHeadingSection, EditHeading, EditTable, EditTHead, EditTBody, EditTR, EditTH, EditTD, DFlex, MoveButtons } from './styledEdit'
import { TableWrapper, Input, Select, EditButton, EditIcon } from './styled'

const types = ['text', 'number', 'date', 'select', 'checkbox']

export default props => {
  const { extra = {}, save, id, effects, runEffect } = props
  const { columns: extraColumns = [] } = extra

  const [mounted, setMounted] = useState(false)
  const [columns, setColumns] = useState(extraColumns)
  const debouncedColumns = useDebounce(columns, 100, { trailing: true })

  useEffect(() => {
    if (mounted) {
      if (!columns.length) {
        save({ _id: id, key: 'defaultRows', value: [] })
      }
      save({ _id: id, key: 'columns', value: debouncedColumns })
    }
  }, [debouncedColumns])

  useEffect(() => {
    runEffect({ source: 'Components/InputTable/getValidations', effectId: id })

    setMounted(true)
  }, [])

  const onChange = (key, value, colIdx, optIdx = undefined) => {
    setColumns(produce(draft => {
      if (key === 'type' && value === 'select') {
        draft[colIdx].options = ['']
      }

      if (optIdx !== undefined) {
        draft[colIdx][key][optIdx] = value
        return
      }

      draft[colIdx][key] = value
    }))
  }

  const addColumn = () => {
    const newColumn = {
      id: uuid(),
      label: 'Label',
      type: 'text',
      sum: false,
      required: false,
      validation: ''
    }

    setColumns(produce(draft => {
      draft.push(newColumn)
    }))
  }

  const moveColumn = (colIdx, up = false) => {
    setColumns(produce(draft => {
      const columnToMove = draft.splice(colIdx, 1)[0]
      draft.splice(up ? colIdx - 1 : colIdx + 1, 0, columnToMove)
    }))
  }

  const deleteColumn = colIdx => {
    setColumns(produce(draft => {
      draft.splice(colIdx, 1)
    }))
  }

  const addOption = colIdx => {
    setColumns(produce(draft => {
      draft[colIdx].options.push('')
    }))
  }

  const deleteOption = (colIdx, optIdx) => {
    setColumns(produce(draft => {
      draft[colIdx].options.splice(optIdx, 1)
    }))
  }

  const validations = effects[id] ? effects[id] : []

  return (
    <EditWrapper>
      <EditHeadingSection>
        <EditHeading>Columns</EditHeading>
      </EditHeadingSection>
      <TableWrapper>
        <EditTable>
          <EditTHead>
            <EditTR>
              <EditTH></EditTH>
              <EditTH>Label</EditTH>
              <EditTH>Type</EditTH>
              {
                columns.some(({ type }) => type === 'select') && (
                  <EditTH>Options</EditTH>
                )
              }
              {
                columns.some(({ type }) => type === 'number') && (
                  <EditTH>Sum</EditTH>
                )
              }
              <EditTH>Required</EditTH>
              {
                columns.some(({ type }) => type === 'text') && (
                  <EditTH>Validation</EditTH>
                )
              }
              <EditTH>
                <EditButton onClick={addColumn}>
                  <EditIcon className="far fa-plus" />
                </EditButton>
              </EditTH>
            </EditTR>
          </EditTHead>
          <EditTBody>
            {
              columns.map(({ label, type, options = [], sum, required, validation }, colIdx) => (
                <EditTR key={colIdx}>
                  <EditTD moveButtons>
                    <MoveButtons>
                      <EditButton onClick={() => moveColumn(colIdx, true)} disabled={!colIdx}>
                        <EditIcon className="far fa-chevron-up" />
                      </EditButton>
                      <EditButton onClick={() => moveColumn(colIdx)} disabled={columns.length - 1 === colIdx}>
                        <EditIcon className="far fa-chevron-down" />
                      </EditButton>
                    </MoveButtons>
                  </EditTD>
                  <EditTD>
                    <Input value={label} onChange={e => onChange('label', e.target.value, colIdx)} />
                  </EditTD>
                  <EditTD>
                    <Select value={type} onChange={e => onChange('type', e.target.value, colIdx)} builder>
                      {
                        types.map((t, typeIdx) => <option key={typeIdx} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)
                      }
                    </Select>
                  </EditTD>
                  {
                    columns.some(({ type }) => type === 'select') && (
                      <EditTD>
                        {
                          type === 'select' && options.length && (
                            options.map((option, optIdx) => (
                              <DFlex>
                                <Input key={optIdx} value={option} onChange={e => onChange('options', e.target.value, colIdx, optIdx)} />
                                {
                                  !optIdx
                                    ? (
                                      <EditButton onClick={() => addOption(colIdx)}>
                                        <EditIcon className="far fa-plus" />
                                      </EditButton>
                                    )
                                    : (
                                      <EditButton onClick={() => deleteOption(colIdx, optIdx)}>
                                        <EditIcon className="far fa-times" />
                                      </EditButton>
                                    )
                                }
                              </DFlex>
                            ))
                          )
                        }
                      </EditTD>
                    )
                  }
                  {
                    columns.some(({ type }) => type === 'number') && (
                      <EditTD checkbox>
                        {
                          type === 'number' && <input type="checkbox" checked={sum} onChange={() => onChange('sum', !sum, colIdx)} />
                        }
                      </EditTD>
                    )
                  }
                  <EditTD checkbox>
                    <input type="checkbox" checked={required} onChange={() => onChange('required', !required, colIdx)} />
                  </EditTD>
                  {
                    columns.some(({ type }) => type === 'text') && (
                      <EditTD>
                        {
                          type === 'text' && (
                            <Select value={validation} onChange={e => onChange('validation', e.target.value, colIdx)}>
                              <option value="">None</option>
                              {
                                validations.map(({ _id, name }, i) => <option key={i} value={_id}>{name}</option>)
                              }
                            </Select>
                          )
                        }
                      </EditTD>
                    )
                  }
                  <EditTD>
                    <EditButton onClick={() => deleteColumn(colIdx)}>
                      <EditIcon className="far fa-times" />
                    </EditButton>
                  </EditTD>
                </EditTR>
              ))
            }
          </EditTBody>
        </EditTable>
      </TableWrapper>
      {
        columns.length > 0 && (
          <>
            <EditHeadingSection>
              <EditHeading>Default rows</EditHeading>
            </EditHeadingSection>
            <InputTable {...props} />
          </>
        )
      }
    </EditWrapper>
  )
}